import { useState, useEffect } from 'react';
import { GlassCard } from '@/components/design/GlassCard';
import { NeonButton } from '@/components/design/NeonButton';
import { GlassTabs } from '@/components/design/GlassTabs';
import {  MessageSquare, Calendar, Shield, Trash2, Pin, MoreHorizontal, Plus } from 'lucide-react';
import { AdminAPI } from '@/services/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const EVENTS = [
  { id: 1, title: 'Weekly Town Hall', date: 'Fri, 6 PM', type: 'Recurring' },
  { id: 2, title: 'Expert AMA Session', date: 'Mon, 3 PM', type: 'One-time' },
];

export const CommunityPage = () => {
  const [activeTab, setActiveTab] = useState('discussions');
  const [discussions, setDiscussions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Delete Modal State
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<any>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const fetchDiscussions = async () => {
    setLoading(true);
    try {
        const res = await AdminAPI.community.getDiscussions();
        setDiscussions(res.data);
    } catch (error) {
        toast.error("Failed to load discussions");
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'discussions') {
        fetchDiscussions();
    }
  }, [activeTab]);

  const handleDeleteClick = (discussion: any) => {
    setSelectedDiscussion(discussion);
    setDeleteReason('');
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedDiscussion) return;
    if (!deleteReason.trim()) {
        toast.error("Please provide a reason");
        return;
    }

    try {
        await AdminAPI.community.deleteDiscussion(selectedDiscussion.id, deleteReason);
        toast.success("Discussion deleted and user notified");
        setIsDeleteOpen(false);
        fetchDiscussions();
    } catch (error) {
        toast.error("Failed to delete discussion");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Community</h2>
          <p className="text-muted-foreground">Manage discussions, events, and guidelines.</p>
        </div>
        <GlassTabs 
          tabs={[
            { id: 'discussions', label: 'Discussions' },
            { id: 'events', label: 'Events' },
            { id: 'guidelines', label: 'Guidelines' },
          ]} 
          activeTab={activeTab} 
          onChange={setActiveTab}
          className="w-full md:w-auto min-w-[300px]"
        />
      </div>

      {activeTab === 'discussions' && (
        <GlassCard className="p-0 overflow-hidden">
          {loading ? (
             <div className="p-8 text-center text-muted-foreground">Loading discussions...</div>
          ) : discussions.length === 0 ? (
             <div className="p-8 text-center text-muted-foreground">No active discussions found.</div>
          ) : (
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-white/5">
              <tr>
                <th className="px-6 py-4 font-medium">Topic</th>
                <th className="px-6 py-4 font-medium">Author</th>
                <th className="px-6 py-4 font-medium">Replies</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {discussions.filter(d => !d.isDeleted).map((item) => (
                <tr key={item.id} className="group hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 font-medium text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    {item.title}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{item.author?.username || 'Unknown'}</td>
                  <td className="px-6 py-4 text-muted-foreground">{item._count?.comments || 0}</td>
                  <td className="px-6 py-4">
                     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20`}>
                       Active
                     </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-white">
                        <Pin className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(item)}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </GlassCard>
      )}

      {activeTab === 'events' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <NeonButton size="sm"><Plus className="w-4 h-4 mr-2" /> Add Event</NeonButton>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {EVENTS.map((event) => (
              <GlassCard key={event.id} hoverEffect className="flex items-center justify-between p-4">
                 <div className="flex items-center gap-4">
                   <div className="p-3 rounded-xl bg-primary/10 text-primary">
                     <Calendar className="w-6 h-6" />
                   </div>
                   <div>
                     <h3 className="font-semibold">{event.title}</h3>
                     <p className="text-sm text-muted-foreground">{event.date} • {event.type}</p>
                   </div>
                 </div>
                 <NeonButton variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></NeonButton>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'guidelines' && (
        <GlassCard>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Community Guidelines</h3>
          </div>
          <textarea 
            className="w-full h-64 bg-background/50 border border-white/10 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none text-white"
            defaultValue="- Be respectful and constructive 
- Back up claims with credible sources
- Embrace diverse perspectives
- No personal attacks"
          />
          <div className="mt-4 flex justify-end">
            <NeonButton>Save Changes</NeonButton>
          </div>
        </GlassCard>
      )}

      {/* Delete Reason Modal */}
        <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
            <DialogContent className="sm:max-w-md bg-background border-white/10 text-white">
                <DialogHeader>
                    <DialogTitle>Delete Discussion</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                        Why are you deleting <strong>"{selectedDiscussion?.title}"</strong>? 
                        This reason will be sent to the author.
                    </p>
                    <Textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Reason for deletion (e.g. Violation of Rule #3)"
                        className="bg-white/5 border-white/10 focus:ring-primary min-h-[100px]"
                    />
                </div>
                <DialogFooter>
                    <NeonButton variant="ghost" onClick={() => setIsDeleteOpen(false)}>Cancel</NeonButton>
                    <NeonButton variant="primary" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 border-red-500/50 shadow-red-900/20">
                        Delete & Notify
                    </NeonButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>

    </div>
  );
};
